// @ts-nocheck - TODO: Fix complex prop types and DOM query types
/* eslint-disable no-unused-vars, no-undef */
import { useState, useEffect, useRef } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import {
	BsBoxArrowRight as BoxArrowRight,
	BsGear as Gear,
	BsHeart as Heart,
	BsQuestionCircle as QuestionCircle,
	BsShieldCheck as ShieldCheck,
	BsPersonBoundingBox as PersonBoundingBox,
} from 'react-icons/bs';
import { useSelector, useDispatch } from 'react-redux';

import BayAvatar from '../../BayAvatar';
import BaySwitch from '../../BaySwitch';
import BayTooltip from '../../BayTooltip';
import getProfileImageUrl from './getProfileImageUrl';
import LanguageSwitcherButton from './LanguageSwitcherButton';
import ActAsSessionOverlay from './ActAsSessionOverlay';
import stopActingAsRequest from './stopActingAs';
import isActingAsAnotherUser from './isActingAsAnotherUser';
import logOffRequest from './logOff';
import type { TranslationFunction } from '../../types/common';

interface CustomerDidsPrefsQuery {
	did: string;
	call: string;
	text: string;
}

interface CustomerDidsPrefs {
	QUERY?: CustomerDidsPrefsQuery[];
}

interface AgentsAccess {
	Status: string;
	View_Own: string;
}

interface ReqAccess {
	Agents_Access: AgentsAccess;
}

interface User {
	ActingAsPUBLICID: string;
	PUBLICID: string;
	ActingAsIMAGEPATH?: string;
	ActingAsFIRSTNAME?: string;
	ActingAsLASTNAME?: string;
	ActingAsEMAIL?: string;
	ActingAsWEBSITEADDRESS?: string;
	outofoffice?: 'T' | 'F';
	preferences_action?: string;
	showActAsButton?: boolean;
	customersDids?: string[];
	customersDidsOptions?: any;
	customersDidsPrefs?: CustomerDidsPrefs;
}

interface MyAccountClientProps {
	t?: TranslationFunction;
	user?: User;
	reqAccess: ReqAccess;
	openModalFunc?: () => void;
	toggleCloseFunc?: () => void;
	showApiTokenButton?: boolean;
	updateLaunchpadApi?: any;
	selectLaunchpadSettingsData?: any;
	showLaunchpadButton?: boolean;
	enableTranslations?: boolean;
	currentLanguage?: string;
	onLanguageChange?: (langCode: string) => void;
	supportedLanguages?: string[];
	weglotReady?: boolean;
}

// NOTE: The 'user' object is passed as a prop from TopNav,
// and not grabbed directly from the state itself
const MyAccountClient = ({
	// Defaulted because this component now calls t() during render, outside every
	// guard. The prop is optional, so a host that omits it used to be harmless --
	// the only previous call sites sat inside a JSX comment. Same default the
	// sibling ActAsModal uses.
	t = (key) => key,
	user,
	reqAccess,
	openModalFunc,
	toggleCloseFunc,
	showApiTokenButton = false,
	updateLaunchpadApi,
	selectLaunchpadSettingsData,
	showLaunchpadButton = false,
	enableTranslations,
	currentLanguage,
	onLanguageChange,
	supportedLanguages,
	weglotReady,
}: MyAccountClientProps) => {
	const dispatch = useDispatch();
	const launchpadSettingsData = useSelector(selectLaunchpadSettingsData);

	const [isOutOfOffice, setIsOutOfOffice] = useState(false);
	const [stopping, setStopping] = useState(false);
	const [stopError, setStopError] = useState(null);
	const loggingOff = useRef(false);
	const [smsCallOption, setSmsCallOption] = useState('F');
	const [smsTextOption, setSmsTextOption] = useState('None');
	const [showLaunchpadOnLogin, setShowLaunchpadOnLogin] = useState(false);

	const imageUrl = getProfileImageUrl(
		user?.ActingAsIMAGE_DATA,
		user?.ActingAsIMAGEPATH ||
			'https://bay-office-staging.com/global/images/nophoto/nophoto-agents-thumb.jpg',
	);

	useEffect(() => {
		if (!user) {
			return;
		}
		setIsOutOfOffice(user.outofoffice);
		checkForQueryObject();
		// Set the launchpad toggle state from the settings data - default to true if no setting exists
		setShowLaunchpadOnLogin(
			launchpadSettingsData?.launchpadSettings?.navigationSettings
				?.showLaunchpadOnLogin ?? true,
		);
	}, [user, launchpadSettingsData]);

	const updateOOFState = (e) => {
		e.stopPropagation();
		/* eslint-disable-next-line no-undef */
		$.iqAgents_OutOfOffice(
			`${user.ActingAsPUBLICID}`,
			e.target.value,
			`${user.preferences_action}`,
		);
		setIsOutOfOffice(e.target.value);
	};

	const updateSmsOption = (e, smsType) => {
		e.stopPropagation();
		/* eslint-disable-next-line no-undef */
		$.iqDIDPreferences_save(`${user.customersDidsPrefs?.QUERY[0]?.did}`);
		if (smsType === 'call') {
			setSmsCallOption(e.target.value);
		} else {
			setSmsTextOption(e.target.value);
		}
	};

	const checkForQueryObject = () => {
		if (user.customersDidsPrefs && user.customersDidsPrefs?.QUERY?.length > 0) {
			setSmsCallOption(user.customersDidsPrefs?.QUERY[0]?.call);
			setSmsTextOption(user.customersDidsPrefs?.QUERY[0]?.text);
		}
	};

	// Log user off
	// TODO: Refactor away this jQuery stuff. Some day...
	// The guard lives here, not on the markup: every click path routes through this
	// function, and $.iqLogOff() navigates away rather than resolving, so nothing
	// else stops a second click landing during that window and issuing a second
	// doLogout request.
	const logOff = () => {
		if (loggingOff.current) {
			return;
		}

		loggingOff.current = true;

		try {
			logOffRequest('client');
		} catch (error) {
			// Only reached when logout never navigates. Releasing the guard keeps the
			// row retryable instead of inert for the rest of the page's life.
			loggingOff.current = false;

			throw error;
		}
	};

	const setStopActing = (e) => {
		e.stopPropagation();
		stopActingAs();
	};

	const stopActingAs = async () => {
		// Shown on click, not after the round trip -- same overlay ActAsModal
		// shows while starting a session, so the wait looks the same either way.
		setStopping(true);
		setStopError(null);

		const { ok, message } = await stopActingAsRequest();

		// A refused stop leaves the user acting as someone else, so it has to say
		// so rather than just dropping the overlay.
		if (!ok) {
			setStopping(false);
			setStopError(message || t('stopActingAsFailed'));
		}
	};

	const openUserProfile = async (publicID) => {
		toggleCloseFunc();
		/* eslint-disable-next-line no-undef */
		await $.iqAgents_MaintainAgent('Update', publicID, '').then((res) => {
			if (res.STATUS === 0) {
				$('.edit-modal .modal-content').css(
					'padding-top',
					user.ActingAsPUBLICID !== publicID ? 60 : 15,
				);
			}
		});
	};

	const viewProfile = () => {
		return (
			reqAccess.Agents_Access.Status === '0' &&
			reqAccess.Agents_Access.View_Own === 'T'
		);
	};

	const handleLaunchpadToggle = async (checked) => {
		setShowLaunchpadOnLogin(checked.target.checked);

		let kvstoreId;
		// Determine the kvstore ID
		if (user?.parent_account_id && user?.parent_account_id !== '') {
			kvstoreId = user?.parent_account_id;
		} else if (
			user?.ActingAsBAY_PARENT_ACCOUNT_ID &&
			user?.ActingAsBAY_PARENT_ACCOUNT_ID !== ''
		) {
			kvstoreId = user?.ActingAsBAY_PARENT_ACCOUNT_ID;
		} else {
			kvstoreId = user?._companyid;
		}

		// Update the settings in kvstore
		const updatedSettings = {
			...launchpadSettingsData,
			launchpadSettings: {
				...(launchpadSettingsData?.launchpadSettings || {}),
				navigationSettings: {
					...(launchpadSettingsData?.launchpadSettings?.navigationSettings ||
						{}),
					showLaunchpadOnLogin: checked.target.checked,
				},
			},
		};

		try {
			await dispatch(
				updateLaunchpadApi({
					component: 'launchpadSettings',
					realm: 'BAY',
					userId: kvstoreId,
					isCompanyContext: true,
					data: updatedSettings,
				}),
			);
		} catch (error) {
			console.error('Error updating launchpad settings:', error);
			// Revert the local state if the API call fails
			setShowLaunchpadOnLogin(!checked);
		}
	};

	return user ? (
		<>
			<ActAsSessionOverlay
				show={stopping}
				label={t('stoppingSecureActAsSession')}
				name={`${user?.ActingAsFIRSTNAME ?? ''} ${user?.ActingAsLASTNAME ?? ''}`.trim()}
				platform="client"
				t={t}
			/>
			<div
				className={`user-menu-header ${viewProfile() ? 'cursor-pointer' : ''}`}
				onClick={() =>
					viewProfile() ? openUserProfile(user.ActingAsPUBLICID) : void 0
				}
			>
				<BayAvatar
					firstName={user?.ActingAsFIRSTNAME}
					imgUrl={imageUrl}
					lastName={user?.ActingAsLASTNAME}
					size="md"
				/>
				<div className="user-menu-details">
					<div className="user-menu-details-name">{user.ActingAsFIRSTNAME}</div>
					<BayTooltip overlay={user.ActingAsEMAIL} placement="top">
						<div className="user-menu-details-email user-menu-details-email-client text-truncate">
							{user.ActingAsEMAIL}
						</div>
					</BayTooltip>
					{reqAccess.Agents_Access.Status === '0' &&
						reqAccess.Agents_Access.View_Own === 'T' && (
						<div className="user-menu-details-profile-link">View Profile</div>
					)}
				</div>
			</div>
			{/* <!--- Lead Routing ---> */}
			<div className="user-menu-item ps-3">
				<Form className="container">
					<Row>
						<div className="dropdown-header">Lead Routing</div>
						<Form.Group as={Col}>
							<Form.Check
								checked={isOutOfOffice === 'F'}
								id="iq_in_office_button"
								label="In Office"
								name="OutOfOfficeOption"
								type="radio"
								value="F"
								onChange={(e) => updateOOFState(e)}
							/>
						</Form.Group>
						<Form.Group as={Col}>
							<Form.Check
								checked={isOutOfOffice === 'T'}
								id="iq_out_of_office_button"
								label="Out Of Office"
								name="OutOfOfficeOption"
								type="radio"
								value="T"
								onChange={(e) => updateOOFState(e)}
							/>
						</Form.Group>
					</Row>
				</Form>
			</div>
			{/* <!--- SMS ---> */}
			{user.customersDids?.length > 0 && (
				<div className="user-menu-item ps-3">
					{user.customersDidsOptions ? (
						<Form className="container">
							<Row>
								<div className="dropdown-header">Virtual Phone Number</div>
								<Col className="my-2" xs={12}>
									Text
								</Col>
								<Col xs={4}>
									<Form.Check
										checked={smsTextOption === 'T'}
										id={`text_${user.customersDidsPrefs?.QUERY[0]?.did}_t`}
										label="On"
										name="SmsTextOption"
										type="radio"
										value="T"
										onChange={(e) => updateSmsOption(e)}
									/>
								</Col>
								<Col xs={4}>
									<Form.Check
										checked={smsTextOption === 'F'}
										id={`text_${user.customersDidsPrefs?.QUERY[0]?.did}_f`}
										label="Off"
										name="SmsTextOption"
										type="radio"
										value="F"
										onChange={(e) => updateSmsOption(e)}
									/>
								</Col>
							</Row>
							<Row className="mt-3">
								<Col className="my-2" xs={12}>
									Call
								</Col>
								<Form.Group as={Col}>
									<Form.Check
										checked={smsCallOption === 'Cell'}
										id={`call_${user.customersDidsPrefs?.QUERY[0]?.did}_cell`}
										label="Mobile"
										name={`call_${user.customersDidsPrefs?.QUERY[0]?.did}`}
										type="radio"
										value="Cell"
										onChange={(e) => updateSmsOption(e, 'call')}
									/>
								</Form.Group>
								<Form.Group as={Col}>
									<Form.Check
										checked={smsCallOption === 'Phone'}
										id={`call_${user.customersDidsPrefs?.QUERY[0]?.did}_phone`}
										label="Phone"
										name={`call_${user.customersDidsPrefs?.QUERY[0]?.did}`}
										type="radio"
										value="Phone"
										onChange={(e) => updateSmsOption(e, 'call')}
									/>
								</Form.Group>
								<Form.Group as={Col}>
									<Form.Check
										checked={smsCallOption === 'None'}
										id={`call_${user.customersDidsPrefs?.QUERY[0]?.did}_none`}
										label="None"
										name={`call_${user.customersDidsPrefs?.QUERY[0]?.did}`}
										type="radio"
										value="None"
										onChange={(e) => updateSmsOption(e, 'call')}
									/>
								</Form.Group>
							</Row>
						</Form>
					) : (
						<div className="container">
							<Row className="mt-2">
								<div className="inner">
									<Button
										className="preferences-button"
										id="iq_did_preferences_button"
										variant="link"
										onClick={() =>
											$.iqDIDPreferences_maintain(
												`${user.ActingAsPUBLICID}`,
												'F',
											)
										}
									>
										Preferences
									</Button>
								</div>
							</Row>
						</div>
					)}
				</div>
			)}
			<div className="user-menu-item ps-3">
				<div className="container">
					<Row>
						<Col className="dropdown-header" xs={12}>Options</Col>
						{/* <!--- Website ---> */}
						<Col xs={12}>
							<div className={`d-flex ${showApiTokenButton ? 'justify-content-between' : ''} align-items-center`}>
								<div>
									<a href={user.ActingAsWEBSITEADDRESS} rel="noopener noreferrer" target="_blank">
										Website
									</a>
								</div>
								{showApiTokenButton && (
									<div>
										<a href="/?page=apitokens">API Tokens</a>
									</div>
								)}
								<div className={`${!showApiTokenButton ? 'ms-7' : ''}`}>
									<a href="/?page=marketplace">Marketplace</a>
								</div>
							</div>
						</Col >
					</Row>
				</div>
			</div>
			{/* Temporarily hidden - launchpad login setting
			{showLaunchpadButton && (
				<div className='d-flex flex-column gap-3 border-bottom ps-3 p-2'>
					<div className='d-flex flex-row align-items-center'>
						<span className="fw-semibold fs-6 text-dark mt-1">{t('displayLaunchpadUponNextLogin')}</span>
						<BayTooltip
							overlay={t('displayLaunchpadUponNextLoginDesc2')}
							placement="top"
						>
							<QuestionCircle className="ms-2 cursor-pointer" fill="var(--bs-gray-700)" />
						</BayTooltip>
					</div>
					<BaySwitch
						label="Show"
						id="show-launchpad"
						name="show-launchpad"
						checked={showLaunchpadOnLogin}
						onChange={handleLaunchpadToggle}
					/>
				</div>
			)}
			*/}
			{enableTranslations && weglotReady && supportedLanguages?.length > 0 && (
				<div className="user-menu-item ps-3">
					<div className="w-100">
						<BayTooltip overlay={<span>Language / Langue / Idioma</span>}>
							<span className="dropdown-header cursor-pointer">Language</span>
						</BayTooltip>
						<LanguageSwitcherButton
							currentLanguage={currentLanguage}
							supportedLanguages={supportedLanguages}
							onLanguageChange={onLanguageChange}
						/>
					</div>
				</div>
			)}
			{/* Only while a session is actually running -- on a direct login there is
				nothing to stop. The banner offers the same action; this is the one in
				the account menu, which is where people look for it. */}
			{isActingAsAnotherUser(user) && (
				<div className="user-menu-item stop-acting-as-button">
					{/* A real button, not a div with role="button": this is the only
						stop-acting-as control a host gets when withBanners is 'none'
						(the default), so it has to be reachable without a mouse.
						stopPropagation still fires from the keyboard path, which keeps
						the drawer open so the overlay and the refusal alert below stay
						mounted. */}
					<button
						type="button"
						className="inner"
						id="stop_acting_as_button"
						onClick={setStopActing}
					>
						<PersonBoundingBox />
						{t('stopActingAs')}
					</button>
					{/* Beside the control, not at the top of the drawer: this menu
						scrolls, and a refusal painted above the avatar header is
						invisible to the person who just clicked down here. role="alert"
						covers the screen-reader case; position covers the sighted one. */}
					{stopError && (
						<div role="alert" className="stop-acting-as-error text-danger px-2 pb-2">
							{stopError}
						</div>
					)}
				</div>
			)}
			{/* One handler, on the container: a click on the inner div bubbles to it,
				so the whole row stays clickable. A second handler on the inner div
				fired logOff twice per click -- two doLogout requests, four revokes. */}
			<div className="user-menu-item logout-button" onClick={() => logOff()}>
				<div className="inner" id="logout_button" role="button">
					<BoxArrowRight />
					Logout
				</div>
			</div>
		</>
	) : (
		<div className="user-menu-item">No User Data</div>
	);
};

export default MyAccountClient;
